import { Component, HBox, Position, PuzzleTooltip as TpTooltip, VBox, WithTooltip } from "puzzle";
import React, { Fragment, ReactElement } from "react";
import {
  LINE_ITEM,
  QUOTE,
  QUOTES_RESPONSE,
  QUOTES_REVIEW_PROPS,
  QUOTES_REVIEW_STATE,
  RESPONSE,
  SCOPE,
  SCOPE_PARSED,
  VENDOR
} from "./Types";
import * as _ from "lodash";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import classNames from "classnames";
import Big from "big.js";
import UAParser from 'ua-parser-js';
import { calculateLineItemsTotal } from "../../../util/Util";
import Policies from "../../../util/Policies";
import { PuzzleButton, PuzzleCheckbox, Container } from "puzzle";

const parser = new UAParser();
const isLinux = parser.getOS().name === 'Linux';

class QuotesReview extends Component<QUOTES_REVIEW_PROPS, QUOTES_REVIEW_STATE> {
  tooltip: any;
  /**
   * Precision for number rounding
   */
  roundNumberPrecision: number = 0;
  /**
   * Have some user input changes when isDirty = true
   */
  isDirty: boolean = false;

  state: QUOTES_REVIEW_STATE = {
    quotesData: {
      hasGlobalComments: false,
      vendors: [],
      scopes: [],
      quoteConfiguration: {
        roundingMode: '',
        autoQuote: false,
        poEnabled: false,
        poMandatory: false,
        numberPrecision: 0,
        quoteLevel: {
          phaseQuotes: false,
          quoteScope: ''
        }
      },
      quotes: [],
      quoteSupplierCount: 0,
      quoteLanguageCount: 0,
      quoteBatchCount: 0
    },
    parsedData: {
      vendors: [],
      scopes: []
    },
    errorQuoteNotRequested: false
  };
  static defaultProps = {};
  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    this.getData();
  }

  /**
   * Open add/edit po dialog
   */
  addEditPoClick = () => {
    this.dispatch('quote_add_edit_po');
  }

  /**
   * Open approve quote dialog
   */
  approveBtnClick = () => {
    const { quotesData } = this.state;
    let currency = quotesData.quotes[0].currency;
    this.dispatch('openApproveQuotesDialog', '', {
      currency,
      data: quotesData,
      grandTotal: this.calculateQuoteGrandTotal(true, true)
    });
  }

  /**
   * Toggle award vendor action
   * approve or remove approve on all vendor quotes
   */
  awardVendor = (vendorId: number, clearAll: boolean) => {
    let { quotesData } = this.state;
    let quotes = quotesData.quotes;
    let quotesLength = quotes.length;
    let current;
    let statusValid = true;
    while (quotesLength) {
      quotesLength--;
      current = quotes[quotesLength];
      statusValid = !_.includes(['APPROVED', 'REJECTED', 'WITHDRAWN', 'REQUESTED', 'COUNTERED'], current.status);
      if (current.vendorId === vendorId && statusValid && !current.quoteNotConfigured && !current.quoteNotProvided) {
        if (clearAll) {
          delete current.approved;
          current.lineItems = current.lineItems.map((lineItem: LINE_ITEM) => {
            delete lineItem.approved;
            return lineItem;
          });
        } else {
          current.approved = true;
        }
        this.toggleQuotesCheckbox(current);
      }
    }
    this.forceUpdate();
  }

  /**
   * Calculate quote total value
   * @param {Boolean} stripHtmlElement Remove html element, return just plain string
   * @param {Boolean} withoutCurrency Return value of total without currency
   */
  calculateQuoteGrandTotal = (stripHtmlElement: boolean, withoutCurrency: boolean) => {
    const { quotes } = this.state.quotesData;
    let total = Big(0)
    let quotesLength = quotes.length;
    let currentQuote;
    let currentQuoteTotal;
    while (quotesLength) {
      quotesLength--;
      currentQuote = quotes[quotesLength];

      if (currentQuote.status === 'APPROVED' || currentQuote.approved) {
        currentQuoteTotal = calculateLineItemsTotal(currentQuote.lineItems, true, true, false);
        total = total.plus(currentQuoteTotal);
      }

    }
    let currency = currentQuote?.currency;
    let totalFormatted: any = '';
    if (total.toNumber()) {
      totalFormatted = total.toFixed(this.roundNumberPrecision);
      if (currency && !withoutCurrency) {
        totalFormatted = this.getCurrencyFormattedTotal(totalFormatted, currency, stripHtmlElement);
      }
    } else {
      totalFormatted = this.translate('dashTotal');
    }


    return totalFormatted;
  }

  /**
   * Calculate total per vendor
   */
  calculateVendorTotal = (vendorId: number) => {
    const { quotesData } = this.state;
    const { quotes } = quotesData;
    let quotesByVendor = _.filter(quotes, { vendorId });
    let total = Big(0);
    let quotesLength = quotesByVendor.length;
    let current;
    while (quotesLength) {
      quotesLength--;
      current = quotesByVendor[quotesLength];
      if (current.totalWithoutOptionals) {
        total = total.plus(current.totalWithoutOptionals);
      }
    }

    let formattedTotal = this.getCurrencyFormattedTotal(total.toFixed(this.roundNumberPrecision), quotesByVendor[0].currency, false);

    return formattedTotal;
  }

  /**
   * Clear all changes
   */
  clearAllChanges = () => {
    let { quotesData } = this.state;
    quotesData.quotes = quotesData.quotes.map((quote: QUOTE) => {
      delete quote.approved;
      quote.lineItems = quote.lineItems.map((lineItem: LINE_ITEM) => {
        delete lineItem.approved;
        return lineItem;
      });
      return quote;
    });

    this.forceUpdate();
  }

  /**
   * Show clear all confirmation dialog if isDirty
   */
  clearButtonHandler = () => {
    if (this.isDirty) {
      this.dispatch('clearQuoteReviewConfirmation');
    }
  }

  /**
   * Open comments dialog
   */
  commentsClickHandler = (vendorId: number | null) => {
    this.dispatch('openCommentsDialog', '', { vendorId });
  }

  /**
   * Checks if props changed and gets quotes for new submission in that case
   */
  componentDidUpdate(prevProps: QUOTES_REVIEW_PROPS): void {
    const submissionData = this.props.submissionData;
    if (!_.isEqual(prevProps.submissionData, submissionData)) {
      this.getData();
    }
  }

  /**
   * Opens downloadQuoteReport dialog
   */
  downloadQuoteReport = () => {
    const { submissionId } = this.props.submissionData;
    this.dispatch('openDownloadQuote', Position.end, { submissionData: { submissionId } });
  };

  /**
   * Get data from API
   */
  getData = () => {
    const { submissionId, projectId } = this.props.submissionData;
    this.dispatch('getQuotesReview', Position.end, { submissionId, projectId });
  }

  /**
   * Get formatted total with currency
   * @param {string }total
   * @param {string} currency
   * @param {Boolean} stripHtmlElement Return just plain string formatted
   */
  getCurrencyFormattedTotal = (total: string, currency: string, stripHtmlElement: boolean) => {
    let result;
    if (stripHtmlElement) {
      result = this.translate('currencyTotalFormat', { currency, total })
    } else {
      result = (
        <>
          <span className={'pd-quotes-total-currency tp-color-light-LM'}>{currency}</span>
          <span className={'tp-text-medium'}>{total}</span>
        </>
      )
    }

    return result;
  }

  /**
   * Change approve property to all group scopes
   */
  groupCheckboxChange = (scopes: SCOPE_PARSED[], vendorId: number, event: CheckboxChangeEvent) => {
    let { value } = event;
    scopes.forEach((scopeItem: SCOPE_PARSED) => {
      scopeItem.quotes = scopeItem.quotes.map((quoteItem: QUOTE) => {
        let statusValid = !_.includes(['APPROVED', 'REJECTED', 'WITHDRAWN', 'REQUESTED', 'COUNTERED'], quoteItem.status);
        if (quoteItem.vendorId === vendorId && statusValid && !quoteItem.quoteNotConfigured && !quoteItem.quoteNotProvided) {
          quoteItem.approved = value;
          if (value) {
            this.toggleQuotesCheckbox(quoteItem);
          } else {
            quoteItem.lineItems = quoteItem.lineItems.map((lineItem: LINE_ITEM) => {
              lineItem.approved = value;
              return lineItem;
            })
          }
        }
        return quoteItem;
      });
    });

    this.forceUpdate();
  }

  /**
   * Line item checkbox change handler
   */
  lineItemCheckboxChange = (lineItem: LINE_ITEM, event: CheckboxChangeEvent) => {
    const { value } = event;
    lineItem.approved = value;
    this.forceUpdate();
  }

  /**
   * Prepare data for render
   */
  parseDataForRender = (quotesData: QUOTES_RESPONSE) => {
    let parsedScopes = [];
    let scopes = quotesData.scopes;
    let scopesLength = scopes.length;
    let i = 0;
    let current: SCOPE;
    let displayName = null;
    let groupingName = null;
    let groupingActive = false;
    let oldGroupingName = null;
    let qaGroupingName = null;
    while (i < scopesLength) {
      current = scopes[i];
      let vendors = quotesData.vendors;
      let vendorsLength = vendors.length;
      let j = 0;
      let currentVendor: VENDOR;
      let currentQuotes;
      let parsedQuotes = [];
      while (j < vendorsLength) {
        currentVendor = vendors[j];
        // eslint-disable-next-line no-loop-func
        currentQuotes = _.find(quotesData.quotes, (quoteItem: QUOTE) => (quoteItem.quoteScope.scopeId === current.scopeId && quoteItem.vendorId === currentVendor.vendorId))
        if (!currentQuotes) {
          currentQuotes = {
            quoteScope: {
              scopeId: current.scopeId
            },
            vendorId: currentVendor.vendorId,
            quoteNotConfigured: true,
            lineItems: []
          };
        } else if (currentQuotes.status === 'REQUESTED') {
          currentQuotes = {
            ...currentQuotes,
            quoteNotProvided: true
          };
        }
        parsedQuotes.push(currentQuotes);
        j++;
      }

      if (current.batchName || current.phaseName) {
        groupingActive = true;
        qaGroupingName = current.languageName;
        if (current.batchName && current.phaseName) {
          displayName = this.translate('batchPhaseTpl', { batch: current.batchName, phase: current.phaseName });
        } else {
          displayName = current.batchName ? current.batchName : current.phaseName;
        }
        if (oldGroupingName !== current.languageName) {
          oldGroupingName = groupingName = current.languageName;
        } else {
          groupingName = null;
        }
      } else {
        displayName = current.languageName;
        groupingName = null;
      }

      parsedScopes.push({
        displayName: displayName,
        groupingName: groupingName,
        groupingActive: groupingActive,
        scopeId: current.scopeId,
        languageId: current.languageId,
        qaGroupingName,
        quotes: parsedQuotes
      })
      i++;
    }

    let parsedData: any = {
      vendors: quotesData.vendors,
      scopes: parsedScopes
    };

    return parsedData;
  }

  /**
   * Quote checkbox change handler
   */
  quoteCheckboxChange = (item: QUOTE, event: CheckboxChangeEvent) => {
    const { value } = event;
    item.approved = value;
    item.lineItems = item.lineItems.map((lineItem: LINE_ITEM) => {
      lineItem.approved = false;
      return lineItem;
    });

    if (value) {
      this.toggleQuotesCheckbox(item);
    }

    this.forceUpdate();
  }

  /**
   * Quote tab totals renderer
   */
  quotesTabTotalsRenderer = () => (
    <div className="tp-quotes-table-row totals" role="row">
      <div
        className="tp-quote-table-cell tp-sticky-left"
        role="gridcell"
      />
      {this.state.parsedData.vendors.map(this.renderTableFooterTotalItem)}
    </div>
  );

  /**
   * Open rate details dialog
   */
  rateDetailsClick = (vendorId: number | null, languageId: string | null) => {
    let { quotesData } = this.state;
    this.dispatch('openQuoteRateDetails', '', {
      quotesData,
      vendorId,
      languageId
    });
  }

  /**
   * Render optional line item
   */
  renderOptionalLineItem = (item: QUOTE, lineItem: LINE_ITEM) => {
    let total;
    if (lineItem.unit === 'PERCENTAGE') {
      let totalWithoutPercentage = calculateLineItemsTotal(item.lineItems, false, true, false);
      if (lineItem.rate) {
        total = Big(lineItem.rate).div(100).times(totalWithoutPercentage);
      }
    } else {
      total = Big(lineItem.rate).times(lineItem.quantity);
    }
    const optionalRejected = (item.status === 'APPROVED' && !lineItem.accepted) || item.status === 'REJECTED';
    const optionalClasses = classNames(
      'tp-quote-total tp-optional',
      {
        'tp-optional-checked': lineItem.approved || false,
        'tp-optional-rejected': optionalRejected,
      }
    )

    const checkboxClasses = classNames(
      'tp-quote-checkbox tp-quote-checkbox-optional',
      {
        'tp-status-rejected': item.status === 'APPROVED' && !lineItem.accepted,
        'tp-quotes-checkbox-linux': isLinux
      }
    )

    const checked = item.status === 'APPROVED';
    const numberIconClasses = classNames(
      'tp-icon tp-icon-sm',
      {
        'tp-check': checked,
        'tp-remove': !checked || optionalRejected
      }
    )
    let totalWithPrecision = (total || 0).toFixed(this.roundNumberPrecision);
    let totalWithCurrency = this.getCurrencyFormattedTotal(totalWithPrecision, item.currency, false);
    return (
      <Fragment key={`tp-optional-item-${item.quoteScope.scopeId}-${item.vendorId}-${lineItem.name}`}>
        {lineItem.isOptional && (
          <div className={optionalClasses}>
            <div className={'k-flex k-hbox tp-optional-item'}>
              <PuzzleCheckbox
                label={this.translate("SRL-checkboxOptional")}
                className={checkboxClasses}
                disabled={item.status === 'APPROVED' || !item.approved || optionalRejected}
                onChange={this.lineItemCheckboxChange.bind(this, lineItem)}
                checked={lineItem.approved || false}
              />
              <WithTooltip
                className={'k-d-flex tp-text-ellipsis'}
                context={this}
                title={lineItem.localizedName}
                htmlTag={'span'}
              >
                <span className={'tp-optional-item-label tp-text-ellipsis'} title={lineItem.localizedName}>
                  {lineItem.localizedName}
                </span>
              </WithTooltip>
              <TpTooltip
                anchorElement={'target'}
                ref={(el: any) => this.tooltip = el}
                openDelay={1000}
                position="left"
              />
            </div>
            <HBox className={'pd-quote-wrapper'}>
              <div className={'tp-quote-number'}>{totalWithCurrency}</div>
              <i
                aria-hidden="true"
                className={numberIconClasses}
              />
            </HBox>
          </div>
        )}
      </Fragment>
    )
  }

  /**
   * Open request update dialog
   */
  requestUpdate = (vendor: VENDOR) => {
    const { quotesData } = this.state;
    this.dispatch('openRequestQuoteUpdateDialog', '', { quotesData, vendor });
  }

  /**
   * Render total per vendor for table footer
   */
  renderTableFooterTotalItem = (vendor: VENDOR) => {
    const { quotesData } = this.state;
    let total = Big(0);
    let quotes = quotesData.quotes;
    let quotesLength = quotes.length;
    let current;
    let vendorCurrency;
    while (quotesLength) {
      quotesLength--;
      current = quotes[quotesLength];
      if (current.vendorId === vendor.vendorId) {
        if (!vendorCurrency) {
          vendorCurrency = current.currency;
        }
        if (current.status === 'APPROVED' || current.approved) {
          total = total.plus(calculateLineItemsTotal(current.lineItems, true, true, false));
        }
      }
    }

    let formattedTotal: any = '';
    if (vendorCurrency) {
      if (total.toNumber()) {
        let roundedTotal = total.toFixed(this.roundNumberPrecision);
        formattedTotal = this.getCurrencyFormattedTotal(roundedTotal, vendorCurrency, false)
      } else {
        formattedTotal = this.getCurrencyFormattedTotal('', vendorCurrency, false);
      }
    }

    return (
      <div
        role="gridcell"
        className="tp-quote-table-cell k-link tp-no-underline tp-cursor-text"
        key={`tp-quotes-table-vendor-total${vendor.vendorId}`}
      >
        <span className={'pd-totals-label tp-color-light-LM tp-text-semibold'}>{this.translate('totalsLabel')}</span>
        <span className={'tp-text-ellipsis'}>
          {formattedTotal}
        </span>
      </div>
    )
  }
  /**
   * Render group total item
   */
  renderGroupTotalItem = (scope: SCOPE_PARSED, vendor: VENDOR) => {
    let total = Big(0);
    let allGroupScopes = _.filter(this.state.parsedData.scopes, { languageId: scope.languageId });
    let allGroupScopesLength = allGroupScopes.length;

    let allApproved = true;
    let allApprovedBySelection = true;
    let allApprovedByStatus = true;
    let hideCheckbox = true;
    let current;
    let currentQuotes;
    let currentQuotesLength;
    let quote;
    let allNotConfiguredAndNotProvided = true;
    while (allGroupScopesLength) {
      allGroupScopesLength--;
      current = allGroupScopes[allGroupScopesLength];
      currentQuotes = current.quotes;
      currentQuotesLength = currentQuotes.length;
      while (currentQuotesLength) {
        currentQuotesLength--;
        quote = currentQuotes[currentQuotesLength];
        if (quote.vendorId === vendor.vendorId) {
          if (hideCheckbox && (!quote.quoteNotConfigured && !quote.quoteNotProvided)) {
            hideCheckbox = false;
          }
          total = total.plus(calculateLineItemsTotal(quote.lineItems, true, true, true));
          if (!quote.quoteNotConfigured && !quote.quoteNotProvided && !_.includes(['REJECTED', 'WITHDRAWN', 'COUNTERED'], quote.status)) {
            allNotConfiguredAndNotProvided = false;
            if (quote.status !== 'APPROVED') {
              allApprovedByStatus = false;
            }
            if (!quote.approved) {
              allApprovedBySelection = false;
            }

            if (quote.status !== 'APPROVED' && !quote.approved) {
              allApproved = false;
            }
          }
        }
      }
    }

    if (allNotConfiguredAndNotProvided) {
      allApproved = false;
      allApprovedBySelection = false;
      allApprovedByStatus = false;
    }

    let cellClassNames = classNames(
      'tp-quote-table-cell',
      {
        'tp-quote-table-cell-approved': allApprovedBySelection,
        'tp-quote-table-cell-approvedByStatus': allApprovedByStatus
      }
    );
    const numberIconClasses = classNames(
      'tp-icon tp-icon-sm',
      {
        'tp-check': allApprovedByStatus,
        'tp-remove': !allApprovedByStatus
      }
    )
    let currency = _.find(this.state.quotesData.quotes, { vendorId: vendor.vendorId })?.currency;
    let formattedTotal;
    if (total.toNumber()) {
      formattedTotal = this.getCurrencyFormattedTotal(total.toFixed(this.roundNumberPrecision), currency || '', false);
    }

    return (
      <div
        className={cellClassNames}
        role="gridcell"
        key={`tp-quotes-review-group-total-${scope.scopeId}-${vendor.vendorId}`}
      >
        <div className={'tp-quote-group-total'}>
          {
            hideCheckbox ? (
              <div />
            ) : (
              <PuzzleCheckbox
                disabled={allApprovedByStatus}
                label={this.translate("SRL-checkboxGroupTotal")}
                onChange={this.groupCheckboxChange.bind(this, allGroupScopes, vendor.vendorId)}
                checked={allApproved}
              />
            )
          }
          <HBox className={'pd-quote-wrapper'}>
            <div className={'tp-quote-number tp-text-ellipsis'}>{formattedTotal}</div>
            {!!total.toNumber() && <i
              aria-hidden="true"
              className={numberIconClasses}
            />}
          </HBox>
        </div>
      </div>
    )
  }

  /**
   * Set data to state
   */
  setData = (response: RESPONSE) => {
    switch (response.status) {
      case 200:
        let quotesData = response.data;
        this.roundNumberPrecision = quotesData.quoteConfiguration.numberPrecision;
        const parsedData = this.parseDataForRender(quotesData);
        this.setState({
          quotesData,
          parsedData,
          errorQuoteNotRequested: false
        });
        break;
      case 428:
        this.setState({
          errorQuoteNotRequested: true
        });
        break;
      default:
        break;
    }
  }

  /**
   * Quote table body grouping row renderer
   */
  tableGroupingRowRenderer = (item: SCOPE_PARSED): ReactElement => (
    <div className={'tp-quotes-table-row tp-quotes-table-row-group'} role="row">
      {item.groupingName && (
        <>
          <div className={'tp-quote-table-cell tp-sticky-left'} role="gridcell">
            <div className={'k-vbox k-align-items-end'}>
              <div className={'tp-quote-group-name k-text-right'}>{item.groupingName}</div>
            </div>
          </div>
          {this.state.quotesData.vendors.map(this.renderGroupTotalItem.bind(this, item))}
        </>
      )}
    </div>
  );

  /**
   * Quote table body row renderer
   */
  tableRowRenderer = (item: SCOPE_PARSED, index: number, array: SCOPE_PARSED[]): ReactElement => {
    const cellClass = classNames(
      'tp-quote-table-cell tp-sticky-left',
      {
        'k-justify-content-center': !item.groupingActive,
        'k-justify-content-start': item.groupingActive
      }
    );

    const rowClass = classNames(
      'tp-quotes-table-row',
      {
        [`fake-qa-class-${item.qaGroupingName}`]: !!item.qaGroupingName,
        'tp-quotes-table-row-framed': !item.groupingActive,
        'tp-last': index === array.length - 1
      }
    );
    return (
      <Fragment key={`tp-quotes-review-body-row-${item.scopeId}-${index}`}>
        {item.groupingActive && this.tableGroupingRowRenderer(item)}
        <div
          className={rowClass}
          role="row"
        >
          <div className={cellClass} role="gridcell">
            <div className={`${item.groupingActive ? 'tp-quote-batch' : 'tp-quote-language'}`}>{item.displayName}</div>
          </div>
          {item.quotes.map(this.tableBodyCellRenderer)}
        </div>
      </Fragment>
    );
  }


  /**
   * Quote table body cell renderer
   */
  tableBodyCellRenderer = (item: QUOTE): ReactElement => {
    const quoteCellClass = classNames(
      'tp-quote-table-cell',
      {
        'tp-quote-not-configured': item.quoteNotConfigured,
        'tp-quote-not-provided': item.quoteNotProvided,
        'tp-quote-update-received': item.status === 'UPDATED',
        'tp-quote-update-requested': item.status === 'COUNTERED',
        'tp-quote-rejected': item.status === 'REJECTED' || item.status === 'WITHDRAWN',
        'tp-quote-approved': item.approved,
        'tp-quote-approvedByStatus': item.status === 'APPROVED'
      });
    let totalWithPrecision = (item.totalWithoutOptionals || 0).toFixed(this.roundNumberPrecision);
    let totalWithCurrency = this.getCurrencyFormattedTotal(totalWithPrecision, item.currency, false);

    let iconCls;
    if (item.status === 'APPROVED') {
      iconCls = 'tp-check';
    } else if (item.status === 'PROVIDED' || item.approved || item.status === 'REJECTED') {
      iconCls = 'tp-remove';
    } else if (item.status === 'COUNTERED') {
      iconCls = 'tp-undo-flat'
    } else if (item.status === 'UPDATED') {
      iconCls = 'tp-update'
    }

    const numberIconClasses = classNames(
      'tp-icon tp-icon-sm',
      iconCls
    )
    return (
      <div
        className={quoteCellClass}
        key={`tp-quotes-review-body-row-item-${item.quoteScope.scopeId}-${item.vendorId}`}
        role="gridcell"
      >
        {item.quoteNotConfigured ? (
          <Container
            alignItemsCenter
            hBox
            justifyContentEnd
            wrap
            className={'tp-100'}>
            <span>{this.translate('na')}</span>
          </Container>
        ) : item.quoteNotProvided ? (
          <Container
            alignItemsCenter
            hBox
            justifyContentEnd
            wrap
            className={'tp-100'}>
            <span className={'tp-text-medium'}>{this.translate('notProvidedLabel')}</span>
            <i
              aria-hidden={'true'}
              className={'tp-icon tp-icon-sm tp-error-outline'}
            />
          </Container>
        ) : (
          <>
            <Container
              vBox
              className={'tp-quote-total'}>
              <Container
                alignItemsCenter
                flex1
                hBox
                justifyContentBetween
                className={'tp-100'}
              >
                {item.status === 'COUNTERED' ? <div /> : <PuzzleCheckbox
                  label={this.translate("SRL-checkboxLabel")}
                  className={'tp-quote-checkbox tp-round tp-radio-like'}
                  disabled={item.status === 'APPROVED' || item.status === 'REJECTED' || item.status === 'WITHDRAWN'}
                  onChange={this.quoteCheckboxChange.bind(this, item)}
                  checked={item.status === 'APPROVED' || item.approved || false}
                />}
                <Container
                  alignItemsCenter
                  hBox
                  justifyContentEnd
                  gap={1}
                >
                  {item.hasComments && (
                    <PuzzleButton
                      fillMode="flat"
                      iconClass="tp-icon tp-comments"
                      size={'small'}
                      onClick={this.commentsClickHandler.bind(this, item.vendorId)}
                    />
                  )}
                  <HBox className={'pd-quote-wrapper'}>
                    <div className={'tp-quote-number tp-text-ellipsis'}>{totalWithCurrency}</div>
                    <i
                      aria-hidden="true"
                      className={numberIconClasses}
                    />
                  </HBox>
                </Container>
              </Container>
            </Container>
            {item.lineItems.map(this.renderOptionalLineItem.bind(this, item))}
          </>
        )}
      </div>
    );
  }

  /**
   * Quote table header cell renderer
   */
  tableHeaderCellRenderer = (vendor: VENDOR): ReactElement => {
    let quotes: QUOTE[] = [];
    this.state.parsedData.scopes.forEach((scope: SCOPE_PARSED) => {
      quotes = quotes.concat(scope.quotes);
    });
    let quotesLength = quotes.length;
    let current;
    let approvedAll = true;
    let disabled = true;
    let ignoredStatuses = ['REJECTED', 'WITHDRAWN', 'COUNTERED', 'REQUESTED', 'APPROVED'];
    let minOneApproved = false;
    let allWithErrors = true;

    while (quotesLength) {
      quotesLength--;
      current = quotes[quotesLength];
      if (current.vendorId === vendor.vendorId) {
        if (!minOneApproved) {
          minOneApproved = !!current.approved;
        }
        if (approvedAll && !_.includes([...ignoredStatuses, undefined], current.status) && !current.approved) {
          approvedAll = false;
        }
        if (disabled && !_.includes([...ignoredStatuses], current.status) && !current.quoteNotProvided && !current.quoteNotConfigured) {
          disabled = false;
        }

        if (allWithErrors && !current.quoteNotProvided && !current.quoteNotConfigured) {
          allWithErrors = false;
        }
      }
    }
    if (!minOneApproved) {
      approvedAll = false;
    }
    let vendorHeaderCls = classNames(
      'tp-quote-table-cell',
      'tp-quote-vendor'
    );
    return (
      <div
        className={vendorHeaderCls}
        key={`tp-quotes-review-header-vendor-${vendor.vendorId}`}
      >
        <span className={'tp-quote-vendor-name tp-text-semibold tp-text-ellipsis'}
              role="columnheader">{vendor.vendorName}</span>
        <Container
          className={'tp-quote-header-total'}
          alignItemsCenter
          hBox
        >
          <PuzzleButton
            aria-label={this.translate('SRL-openRateDetails')}
            titleText={this.translate('SRL-openRateDetails')}
            fillMode='flat'
            size={'small'}
            disabled={allWithErrors}
            iconClass={'tp-icon tp-info'}
            onClick={this.rateDetailsClick.bind(this, vendor.vendorId, null)}
            tooltipPosition={'right'}
            withTooltip
          />
          <span>{this.calculateVendorTotal(vendor.vendorId)}</span>
        </Container>
        <Container
          className={'tp-quote-vendor-actions'}
          alignItemsCenter
          hBox
        >
          <PuzzleButton
            className={'tp-request-btn'}
            disabled={disabled}
            fillMode='link'
            themeColor={'primary'}
            size={'small'}
            onClick={this.requestUpdate.bind(this, vendor)}
          >
            {this.translate('requestUpdate')}
          </PuzzleButton>
          <PuzzleButton
            className={approvedAll ? 'tp-approvedAll-btn' : 'tp-awardAll-btn'}
            size={'small'}
            disabled={disabled}
            fillMode={approvedAll ? 'solid' : 'link'}
            themeColor={approvedAll ? 'error' : 'primary'}
            onClick={this.awardVendor.bind(this, vendor.vendorId, approvedAll)}
          >
            {approvedAll ? this.translate('clearAll') : this.translate('awardAll')}
          </PuzzleButton>
        </Container>
      </div>
    )
  };

  /**
   * Quotes table header renderer
   */
  tableHeaderRenderer = () => {
    const { quotesData } = this.state;
    return (
      <div className={'tp-quotes-table-row tp-quotes-table-header-row tp-sticky-top'} role="row">
        <div className={'tp-quote-table-cell tp-empty tp-sticky-top-left'} role="columnheader" />
        {quotesData.vendors?.map(this.tableHeaderCellRenderer)}
      </div>
    );
  }

  /**
   * Quotes tab header renderer
   */
  tabHeaderRenderer = () => {
    return (
      <div className={'tp-quotes-tab-header'}>
        <div className={'tp-quotes-info-bar'} />
        {this.state.quotesData.hasGlobalComments && (
          <div className={'tp-quotes-tab-header-btn-wrap'}>
            <PuzzleButton
              fillMode={'flat'}
              themeColor={'primary'}
              reverse
              size={'small'}
              iconClass={'tp-icon tp-comments'}
              onClick={this.commentsClickHandler.bind(this, null)}
            >
              {this.translate('commentsBtnLabel')}
            </PuzzleButton>
          </div>
        )}
      </div>
    );
  };

  /**
   * Quotes table renderer
   */
  tableRenderer = () => {
    const { parsedData } = this.state;
    return (
      <div className={'tp-quotes-table'} role="grid">
        {this.tableHeaderRenderer()}
        <div className={'tp-quotes-table-body'} role="rowgroup">
          {parsedData.scopes.map(this.tableRowRenderer)}
          {this.quotesTabTotalsRenderer()}
        </div>
      </div>
    );
  };

  /**
   * Quotes tab footer renderer
   */
  tabFooterRenderer = () => {
    const { projectId } = this.props.submissionData;
    let {
      quoteConfiguration,
      quotes
    } = this.state.quotesData;
    const {
      poEnabled
    } = quoteConfiguration;
    let quotesLength = quotes.length;
    let haveApproved = false;
    let allApprovedOrRejected = true;
    let enableDownload = false;
    let current;
    let i = 0;
    while (i < quotesLength) {
      current = quotes[i];
      if (!haveApproved) {
        haveApproved = !!current.approved;
      }
      if (!enableDownload) {
        enableDownload = _.includes(['APPROVED', 'PROVIDED', 'UPDATED'], current.status);
      }
      if (allApprovedOrRejected) {
        allApprovedOrRejected = _.includes(['APPROVED', 'REJECTED'], current.status)
        if (!allApprovedOrRejected && current.status === 'REQUESTED') {
          allApprovedOrRejected = !!_.find(quotes, { quoteScope: current.quoteScope, status: 'APPROVED' });
        }
      }
      i++;
    }

    this.isDirty = haveApproved;
    return (
      <div className={'tp-quotes-tab-footer k-flex-wrap'}>
        <PuzzleButton
          disabled={!haveApproved}
          onClick={this.clearButtonHandler}
          fillMode={'flat'}
        >
          {this.translate('clearButtonLabel')}
        </PuzzleButton>
        <div className={'tp-quotes-tab-footer-right k-d-flex k-flex-wrap'}>
          <VBox
            alignItemsEnd
            className={'tp-quote-grand-total tp-text-semibold'}
          >
            <span>{this.translate('totalsLabel')}</span>
            <span className={'tp-currency'}>{this.calculateQuoteGrandTotal(false, false)}</span>
          </VBox>
          <div className={'tp-quotes-tab-footer-right-btn'}>
            {allApprovedOrRejected && quotesLength ? (
              poEnabled &&
              <PuzzleButton
                themeColor={'primary'}
                onClick={this.addEditPoClick}
              >
                {this.translate('addEditPo')}
              </PuzzleButton>
            ) : (
              Policies.inProject(projectId)?.hasApproveQuotePolicy &&
              <PuzzleButton
                disabled={!haveApproved}
                themeColor={'primary'}
                onClick={this.approveBtnClick}
              >
                {this.translate('approveBtnLabel')}
              </PuzzleButton>
            )}
            {Policies.hasLookupQuotePolicy &&
              <PuzzleButton
                disabled={!enableDownload}
                onClick={this.downloadQuoteReport}
                aria-label={this.translate('SRL-approveBtnLabel')}
                iconClass={'tp-icon tp-download'}
                themeColor={'primary'}
              />}
          </div>
        </div>
      </div>
    )
  };

  /**
   * Toggle quotes checkbox on same scope
   */
  toggleQuotesCheckbox = (quote: QUOTE) => {
    let { scopes } = this.state.parsedData;
    let scopesLength = scopes.length;
    let currentScope;
    let matchedScope: SCOPE_PARSED | boolean = false;
    while (scopesLength && !matchedScope) {
      scopesLength--;
      currentScope = scopes[scopesLength];
      matchedScope = _.findIndex(currentScope.quotes, quote) !== -1 ? currentScope : false;
    }

    if (matchedScope) {
      matchedScope.quotes.map((quoteItem: QUOTE) => {
        if (quoteItem !== quote) {
          quoteItem.approved = false;
          quoteItem.lineItems.forEach((lineItem: LINE_ITEM) => {
            delete lineItem.approved;
            return lineItem;
          });
        }
        return quoteItem;
      })
    }
  }

  render() {
    let { quotesData, errorQuoteNotRequested } = this.state;
    let quoteScope = quotesData.quoteConfiguration.quoteLevel.quoteScope;
    let errorText;
    if (errorQuoteNotRequested) {
      errorText = this.translate('quotesNotRequested');
    } else if (quoteScope === 'SUBMISSION') {
      errorText = this.translate('submissionLevelWarning');
    } else if (quotesData.quoteConfiguration.autoQuote) {
      errorText = this.translate('autoQuotesNotSupported');
    }
    return (
      <>
        {errorText ? (
          <Container
            alignItemsCenter
            className="tp-quote-tab-placeholder"
            vBox
            justifyContentCenter
          >
            <div className="tp-quote-not-provided-img" />
            <span className='tp-quote-not-provided-text tp-text-semibold tp-font-size-xl k-text-center'>
              {errorText}
            </span>
          </Container>
        ) : (
          <>
            {this.tabHeaderRenderer()}
            {this.tableRenderer()}
            {this.tabFooterRenderer()}
          </>
        )}
      </>
    );
  }
}

export default QuotesReview;
